import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useToast } from '../ui/Toast';

interface BusyResult {
  user: { id: string; email: string; name?: string | null };
  busy: Array<{
    eventId: string;
    summary: string;
    startsAt: string;
    endsAt: string;
    allDay: boolean;
    calendar: { name: string; color: string };
  }>;
}

export function FreeBusyPage() {
  const today = new Date();
  const [from, setFrom] = useState(toLocalInput(today));
  const [to, setTo] = useState(toLocalInput(new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)));
  const [result, setResult] = useState<BusyResult | null>(null);
  const toast = useToast();

  const check = async () => {
    try {
      const data = await api.get<BusyResult>(`/api/freebusy?from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`);
      setResult(data);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Free/Busy konnte nicht geladen werden');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-h3">Free/Busy</h1>
        <div className="text-small text-ink-muted">Verfuegbarkeit aus eigenen und freigegebenen Kalendern pruefen.</div>
      </div>
      <section className="card grid grid-cols-[1fr,1fr,auto] gap-3 items-end">
        <div>
          <label className="label">Von</label>
          <input type="datetime-local" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">Bis</label>
          <input type="datetime-local" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={check}>Pruefen</button>
      </section>
      {result && (
        <section className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-h4">{result.user.name ?? result.user.email}</h2>
            <span className="badge badge-muted">{result.busy.length} belegte Zeiten</span>
          </div>
          <div className="grid gap-2">
            {result.busy.map((item) => (
              <Link key={item.eventId} to={`/events/${item.eventId}`} className="bg-surface-elevated border border-border rounded-md px-3 py-2 grid grid-cols-[180px,1fr,160px] gap-3 hover:bg-white/[0.04]">
                <span className="font-mono text-small text-ink-secondary">{formatRange(item.startsAt, item.endsAt, item.allDay)}</span>
                <span>{item.summary}</span>
                <span className="inline-flex items-center gap-2 text-small text-ink-muted">
                  <span className="w-2 h-2 rounded-full" style={{ background: item.calendar.color }} />
                  {item.calendar.name}
                </span>
              </Link>
            ))}
            {result.busy.length === 0 && <div className="text-small text-ink-muted">Keine belegten Zeiten im Zeitraum.</div>}
          </div>
        </section>
      )}
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatRange(start: string, end: string, allDay: boolean) {
  if (allDay) return 'ganztags';
  return `${new Date(start).toLocaleString('de-DE')} - ${new Date(end).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}
